import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';

import { calcPolicyTotalCost } from '@shared';
import { selectClient } from '@client/model/selectors';

import { useAddPolicyFromQuote } from './use-add-policy-from-quote';

/**
 * Hook that calculates the total policy cost by injecting the selected policy
 * into a client clone and running calcPolicyTotalCost.
 *
 * This leverages the existing calcPolicyTotalCost logic which handles:
 * - Single Pay (via policyLumpSumPayment)
 * - 10-Pay (via policyLimitedPayYears)
 * - Monthly/Annually recurring premiums (via policyPremiumMonthlyCost)
 */
export function usePolicyTotalCost(): number {
  const client = useSelector(selectClient);
  const { selectedQuote: selectedQuoteInfo } = useAddPolicyFromQuote();

  const policyTotalCost = useMemo(() => {
    if (!client || !selectedQuoteInfo) return 0;

    const { policyFundingSource } = selectedQuoteInfo;

    // Clone client and set the policy on fundingSources.ltcPolicy
    const clientClone = cloneDeep(client);
    clientClone.fundingSources.ltcPolicy = policyFundingSource;

    // Calculate policyTotalCost (mutates clientClone.fundingSources.ltcPolicy)
    calcPolicyTotalCost(clientClone);

    return clientClone.fundingSources.ltcPolicy.policyTotalCost ?? 0;
  }, [client, selectedQuoteInfo]);

  return policyTotalCost;
}
